import { formatBenefit, previewPackageConditions, type CreditBenefit, type ConditionsPreview } from '@/lib/credits-api'
import { ConditionsComparison } from '@/components/subscription-conditions'
import { PackageBenefitsEditor } from '@/components/admin/credit-settings'
import { useMemo, useState, type DragEvent, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArchiveIcon,
  BadgeEuroIcon,
  GripVerticalIcon,
  Loader2Icon,
  PencilIcon,
  PlusIcon,
  RefreshCwIcon,
  SearchIcon,
  Trash2Icon,
} from 'lucide-react'
import { toast } from 'sonner'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  archiveAdminSubscriptionPackage,
  deleteAdminSubscriptionPackage,
  getAdminSubscriptionPackages,
  retrySubscriptionPriceUpdates,
  reorderAdminSubscriptionPackages,
  rolloutAdminSubscriptionPackagePrice,
  saveAdminSubscriptionPackage,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import { formatCreditAmount, parseCreditAmount } from '@/lib/subscriptions'
import type {
  AdminSubscriptionPackage,
  AdminSubscriptionPackageInput,
  SubscriptionBillingMode,
} from '@/types/domain'

type DialogMode = 'create' | 'edit'

function formatEuro(cents: number | null): string {
  if (cents == null) return '-'
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`
}

function parsePriceCents(value: string): number | null {
  const amount = Number(value.replace(',', '.'))
  if (!Number.isFinite(amount) || amount < 0) {
    return null
  }
  return Math.round(amount * 100)
}

function packageStatus(pkg: AdminSubscriptionPackage) {
  if (pkg.archived_at) {
    return { label: 'Archiviert', variant: 'secondary' as const }
  }
  if (pkg.is_active) {
    return { label: 'Aktiv', variant: 'default' as const }
  }
  return { label: 'Entwurf/Inaktiv', variant: 'secondary' as const }
}

function stripeStatus(pkg: AdminSubscriptionPackage) {
  if (!pkg.stripe_price_id) {
    return { label: 'Preis fehlt', variant: 'destructive' as const }
  }
  if (pkg.stripe_price_validation_error) {
    return { label: 'Fehler', variant: 'destructive' as const }
  }
  if (pkg.stripe_price_validated_at) {
    return { label: 'Geprüft', variant: 'default' as const }
  }
  return { label: 'Nicht geprüft', variant: 'secondary' as const }
}

export function AdminSubscriptionPackagesPage() {
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [kindFilter, setKindFilter] = useState<SubscriptionBillingMode>('payment')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'archived'>('all')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<DialogMode>('create')
  const [editingPackage, setEditingPackage] = useState<AdminSubscriptionPackage | null>(null)
  const [archiveTarget, setArchiveTarget] = useState<AdminSubscriptionPackage | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<AdminSubscriptionPackage | null>(null)
  const [draggedPackageId, setDraggedPackageId] = useState<string | null>(null)
  const [dragOverPackageId, setDragOverPackageId] = useState<string | null>(null)

  const [benefits, setBenefits] = useState<CreditBenefit[]>([])
  const [requiredCourseId, setRequiredCourseId] = useState<string | null>(null)
  const [review, setReview] = useState<{ preview: ConditionsPreview; payload?: AdminSubscriptionPackageInput; packageId: string } | null>(null)
  const [reviewLoading, setReviewLoading] = useState(false)

  async function reviewConditions(packageId: string, payload?: AdminSubscriptionPackageInput) {
    setReviewLoading(true)
    try {
      const preview = await previewPackageConditions(packageId, payload ? {
        benefits: payload.benefits ?? [], price_cents: payload.priceCents,
        stripe_price_id: payload.stripePriceId ?? null, required_course_id: payload.requiredCourseId ?? null,
      } : undefined)
      setReview({ preview, payload, packageId })
    } catch (error) { toast.error(getErrorMessage(error)) }
    finally { setReviewLoading(false) }
  }

  const [name, setName] = useState('')
  const [billingMode, setBillingMode] = useState<SubscriptionBillingMode>('payment')
  const [price, setPrice] = useState('')
  const [stripePriceId, setStripePriceId] = useState('')
  const [credits, setCredits] = useState('1')
  const [weeklyLimit, setWeeklyLimit] = useState('')
  const [features, setFeatures] = useState('')
  const [isFeatured, setIsFeatured] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [applyPriceToExisting, setApplyPriceToExisting] = useState(false)

  const packagesQuery = useQuery({
    queryKey: queryKeys.adminSubscriptionPackages,
    queryFn: getAdminSubscriptionPackages,
  })

  const saveMutation = useMutation({
    mutationFn: (payload: AdminSubscriptionPackageInput) =>
      saveAdminSubscriptionPackage(payload),
    onSuccess: ({ queued }) => {
      setDialogOpen(false)
      setReview(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.adminSubscriptionPackages })
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionPackages })
      toast.success(
        queued > 0
          ? `Angebot gespeichert. ${queued} Abo-Konditionsänderungen wurden eingereiht.`
          : 'Angebot wurde gespeichert.',
      )
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
      if (error instanceof Error && error.message.includes('CONDITIONS_PREVIEW_CHANGED') && review) void reviewConditions(review.packageId, review.payload)
    },
  })

  const archiveMutation = useMutation({
    mutationFn: archiveAdminSubscriptionPackage,
    onSuccess: () => {
      setArchiveTarget(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.adminSubscriptionPackages })
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionPackages })
      toast.success('Angebot wurde archiviert.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const deleteMutation = useMutation({
    mutationFn: deleteAdminSubscriptionPackage,
    onSuccess: () => {
      setDeleteTarget(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.adminSubscriptionPackages })
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionPackages })
      toast.success('Angebot wurde gelöscht.')
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminSubscriptionPackages })
      toast.error(getErrorMessage(error))
    },
  })

  const retryMutation = useMutation({
    mutationFn: retrySubscriptionPriceUpdates,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminSubscriptionPackages })
      toast.success('Fehlgeschlagene Preisänderungen wurden erneut eingereiht.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const rolloutMutation = useMutation({
    mutationFn: rolloutAdminSubscriptionPackagePrice,
    onSuccess: ({ queued }) => {
      setReview(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.adminSubscriptionPackages })
      toast.success(
        queued > 0
          ? `${queued} Bestandsabos wurden für die neuen Konditionen vorgemerkt.`
          : 'Alle Bestandsabos verwenden bereits die aktuellen Konditionen.',
      )
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
      if (review && error instanceof Error && error.message.includes('CONDITIONS_PREVIEW_CHANGED')) void reviewConditions(review.packageId, review.payload)
    },
  })

  const reorderMutation = useMutation({
    mutationFn: reorderAdminSubscriptionPackages,
    onMutate: async (packageIds) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.adminSubscriptionPackages })
      const previous = queryClient.getQueryData<AdminSubscriptionPackage[]>(
        queryKeys.adminSubscriptionPackages,
      )

      if (previous) {
        const positionById = new Map(packageIds.map((id, index) => [id, index]))
        const reordered = [...previous].sort((left, right) => {
          if (left.archived_at && !right.archived_at) return 1
          if (!left.archived_at && right.archived_at) return -1
          if (left.archived_at && right.archived_at) {
            return left.display_order - right.display_order
          }
          return (positionById.get(left.id) ?? 0) - (positionById.get(right.id) ?? 0)
        }).map((pkg) => {
          const position = positionById.get(pkg.id)
          return position == null ? pkg : { ...pkg, display_order: (position + 1) * 10 }
        })

        queryClient.setQueryData(queryKeys.adminSubscriptionPackages, reordered)
      }

      return { previous }
    },
    onError: (error, _packageIds, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.adminSubscriptionPackages, context.previous)
      }
      toast.error(getErrorMessage(error))
    },
    onSuccess: () => {
      toast.success('Reihenfolge wurde gespeichert.')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminSubscriptionPackages })
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionPackages })
    },
  })

  const filteredPackages = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase()
    return (packagesQuery.data ?? []).filter((pkg) => {
      if (pkg.billing_mode !== kindFilter) return false
      if (statusFilter === 'active' && (!pkg.is_active || pkg.archived_at)) return false
      if (statusFilter === 'inactive' && (pkg.is_active || pkg.archived_at)) return false
      if (statusFilter === 'archived' && !pkg.archived_at) return false
      if (!needle) return true
      return [pkg.name, pkg.type, pkg.stripe_price_id ?? '']
        .join(' ')
        .toLowerCase()
        .includes(needle)
    })
  }, [kindFilter, packagesQuery.data, searchTerm, statusFilter])

  const dragAndDropEnabled =
    searchTerm.trim() === '' &&
    statusFilter === 'all' &&
    !reorderMutation.isPending

  function resetDragState() {
    setDraggedPackageId(null)
    setDragOverPackageId(null)
  }

  function handleDragStart(
    event: DragEvent<HTMLTableRowElement>,
    pkg: AdminSubscriptionPackage,
  ) {
    if (!dragAndDropEnabled || pkg.archived_at) {
      event.preventDefault()
      return
    }

    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', pkg.id)
    setDraggedPackageId(pkg.id)
  }

  function handleDragOver(
    event: DragEvent<HTMLTableRowElement>,
    pkg: AdminSubscriptionPackage,
  ) {
    if (!dragAndDropEnabled || pkg.archived_at || !draggedPackageId) {
      return
    }

    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    setDragOverPackageId(pkg.id)
  }

  function handleDrop(
    event: DragEvent<HTMLTableRowElement>,
    targetPackage: AdminSubscriptionPackage,
  ) {
    event.preventDefault()
    const sourceId = draggedPackageId ?? event.dataTransfer.getData('text/plain')
    const sortablePackages = (packagesQuery.data ?? []).filter(
      (pkg) => !pkg.archived_at && pkg.billing_mode === kindFilter,
    )
    const sourceIndex = sortablePackages.findIndex((pkg) => pkg.id === sourceId)
    const targetIndex = sortablePackages.findIndex((pkg) => pkg.id === targetPackage.id)

    resetDragState()
    if (
      !dragAndDropEnabled ||
      targetPackage.archived_at ||
      sourceIndex < 0 ||
      targetIndex < 0 ||
      sourceIndex === targetIndex
    ) {
      return
    }

    const reordered = [...sortablePackages]
    const [movedPackage] = reordered.splice(sourceIndex, 1)
    reordered.splice(targetIndex, 0, movedPackage)
    reorderMutation.mutate(reordered.map((pkg) => pkg.id))
  }

  function resetForm() {
    setEditingPackage(null)
    setName('')
    setBillingMode('payment')
    setPrice('')
    setStripePriceId('')
    setCredits('1')
    setWeeklyLimit('')
    setFeatures('')
    setIsFeatured(false)
    setIsActive(false)
    setApplyPriceToExisting(false)
  }

  function openCreateDialog() {
    resetForm()
    setDialogMode('create')
    setDialogOpen(true)
  }

  function openEditDialog(pkg: AdminSubscriptionPackage) {
    setEditingPackage(pkg)
    setDialogMode('edit')
    setName(pkg.name)
    setBillingMode(pkg.billing_mode)
    setPrice((pkg.price_cents / 100).toFixed(2).replace('.', ','))
    setStripePriceId(pkg.stripe_price_id ?? '')
    setCredits(String(Math.max(0, pkg.credits_included)))
    setWeeklyLimit(pkg.max_sessions_per_week == null ? '' : String(pkg.max_sessions_per_week))
    setFeatures(pkg.features.join('\n'))
    setIsFeatured(pkg.is_featured)
    setIsActive(pkg.is_active)
    setApplyPriceToExisting(false)
    setBenefits(pkg.benefits ?? [])
    setRequiredCourseId(pkg.required_course_id ?? null)
    setDialogOpen(true)
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const priceCents = parsePriceCents(price)
    const parsedCredits = billingMode === 'payment' ? parseCreditAmount(credits, { allowZero: true }) : 0
    const parsedWeeklyLimit =
      billingMode === 'subscription' && weeklyLimit.trim()
        ? parseCreditAmount(weeklyLimit)
        : null

    if (!name.trim()) {
      toast.error('Bitte gib einen Namen ein.')
      return
    }
    if (priceCents == null) {
      toast.error('Bitte gib einen gültigen Preis ein.')
      return
    }
    if (parsedCredits == null) {
      toast.error('Bitte prüfe die enthaltenen Credits.')
      return
    }
    if (billingMode === 'subscription' && weeklyLimit.trim() && parsedWeeklyLimit == null) {
      toast.error('Bitte gib ein gültiges Wochenlimit ein.')
      return
    }

    const displayOrder =
      editingPackage?.display_order ??
      Math.max(
        0,
        ...(packagesQuery.data ?? [])
          .filter((pkg) => pkg.billing_mode === billingMode && !pkg.archived_at)
          .map((pkg) => pkg.display_order),
      ) + 10

    const payload: AdminSubscriptionPackageInput = {
      id: editingPackage?.id,
      name: name.trim(),
      billingMode,
      priceCents,
      stripePriceId: stripePriceId.trim() || null,
      creditsIncluded: parsedCredits,
      maxSessionsPerWeek: parsedWeeklyLimit,
      features: features.split('\n').map((item) => item.trim()).filter(Boolean),
      displayOrder,
      isFeatured,
      isActive,
      applyPriceToExisting:
        billingMode === 'subscription' && applyPriceToExisting,
      ...(editingPackage ? { benefits, requiredCourseId } : {}),
    }
    if (payload.id && payload.applyPriceToExisting) void reviewConditions(payload.id, payload)
    else saveMutation.mutate(payload)
  }


  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Pakete &amp; Abos</h1>
          <p className="text-sm text-muted-foreground">
            Verkaufskatalog, Leistungen und Stripe-Preise verwalten.
          </p>
        </div>
        <Button onClick={openCreateDialog}>
          <PlusIcon className="mr-2 h-4 w-4" />
          Angebot anlegen
        </Button>
      </div>

      <Alert>
        <AlertDescription className="space-y-2">
          <p>
            Paket-Credits gelten nur für neue Käufe. Änderungen an Preis und Wochenkontingent gelten
            für Bestandsabos nur nach ausdrücklicher Übernahme zur nächsten Abrechnung.
          </p>
          <p className="font-medium">
            Tarifwechsel und Kündigungen erfolgen geprüft in der App. Das Stripe-Kundenportal dient Zahlungsdaten und Rechnungen.
          </p>
          <p>
            Ziehe eine Tabellenzeile am Griff nach oben oder unten, um die Reihenfolge in
            FitnessHub und auf der Landingpage zu ändern. Während Suche oder Filter aktiv sind,
            ist die Sortierung deaktiviert.
          </p>
        </AlertDescription>
      </Alert>

      <Tabs
        value={kindFilter}
        onValueChange={(value) => {
          setKindFilter(value as SubscriptionBillingMode)
          resetDragState()
        }}
        className="flex min-h-0 flex-1 flex-col gap-4"
      >
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="payment">
            Pakete ({(packagesQuery.data ?? []).filter((pkg) => pkg.billing_mode === 'payment').length})
          </TabsTrigger>
          <TabsTrigger value="subscription">
            Abos ({(packagesQuery.data ?? []).filter((pkg) => pkg.billing_mode === 'subscription').length})
          </TabsTrigger>
        </TabsList>

      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <CardHeader className="shrink-0 space-y-4">
          <CardTitle className="text-lg">
            {kindFilter === 'payment' ? 'Credit-Pakete' : 'Monatsabos'}
          </CardTitle>
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_12rem]">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Name, Typ oder Stripe-Preis suchen"
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as typeof statusFilter)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="active">Aktiv</SelectItem>
                <SelectItem value="inactive">Entwurf/Inaktiv</SelectItem>
                <SelectItem value="archived">Archiviert</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="min-h-0 flex-1 overflow-hidden">
          {packagesQuery.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <Table containerClassName="h-full max-h-none">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <span className="sr-only">Sortierung</span>
                  </TableHead>
                  <TableHead>Angebot</TableHead>
                  <TableHead>Art</TableHead>
                  <TableHead>Preis</TableHead>
                  <TableHead>Leistung</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Stripe</TableHead>
                  <TableHead>Bestandsabos</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPackages.map((pkg) => {
                  const status = packageStatus(pkg)
                  const stripe = stripeStatus(pkg)
                  return (
                    <TableRow
                      key={pkg.id}
                      draggable={dragAndDropEnabled && !pkg.archived_at}
                      onDragStart={(event) => handleDragStart(event, pkg)}
                      onDragOver={(event) => handleDragOver(event, pkg)}
                      onDrop={(event) => handleDrop(event, pkg)}
                      onDragEnd={resetDragState}
                      className={
                        draggedPackageId === pkg.id
                          ? 'opacity-50'
                          : dragOverPackageId === pkg.id
                            ? 'bg-muted/70'
                            : undefined
                      }
                    >
                      <TableCell className="w-10">
                        <span
                          title={
                            pkg.archived_at
                              ? 'Archivierte Angebote können nicht sortiert werden.'
                              : dragAndDropEnabled
                                ? 'Zeile ziehen, um die Reihenfolge zu ändern.'
                                : 'Zum Sortieren Suche und Filter zurücksetzen.'
                          }
                          className={
                            dragAndDropEnabled && !pkg.archived_at
                              ? 'inline-flex cursor-grab text-muted-foreground active:cursor-grabbing'
                              : 'inline-flex cursor-not-allowed text-muted-foreground/40'
                          }
                        >
                          <GripVerticalIcon className="h-5 w-5" />
                        </span>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{pkg.name}</p>
                      </TableCell>
                      <TableCell>{pkg.billing_mode === 'subscription' ? 'Abo' : 'Credit-Paket'}</TableCell>
                      <TableCell>{formatEuro(pkg.price_cents)}</TableCell>
                      <TableCell>
                        {pkg.benefits?.length ? pkg.benefits.map(formatBenefit).join(' · ') : pkg.billing_mode === 'subscription'
                          ? pkg.max_sessions_per_week == null
                            ? 'Unbegrenzt Credits/Woche'
                            : `${formatCreditAmount(pkg.max_sessions_per_week)} Credits/Woche`
                          : `${formatCreditAmount(pkg.credits_included)} Credits`}
                      </TableCell>
                      <TableCell><Badge variant={status.variant}>{status.label}</Badge></TableCell>
                      <TableCell>
                        <Badge variant={stripe.variant}>{stripe.label}</Badge>
                      </TableCell>
                      <TableCell>
                        {pkg.billing_mode === 'subscription' ? (
                          <div className="space-y-1 text-sm">
                            <p>{pkg.active_subscription_count} aktiv</p>
                            {pkg.legacy_price_count > 0 && (
                              <Badge variant="secondary" className="mt-1">
                                {pkg.legacy_price_count} alter Preis
                              </Badge>
                            )}
                            {pkg.pending_price_update_count > 0 && (
                              <Badge variant="secondary">{pkg.pending_price_update_count} ausstehend</Badge>
                            )}
                            {pkg.failed_price_update_count > 0 && (
                              <Badge variant="destructive">{pkg.failed_price_update_count} fehlgeschlagen</Badge>
                            )}
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <TooltipProvider delayDuration={150}>
                          <div className="flex justify-end gap-2">
                            {pkg.archived_at && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      aria-label="Angebot löschen"
                                      disabled={!pkg.can_delete || deleteMutation.isPending}
                                      onClick={() => setDeleteTarget(pkg)}
                                    >
                                      <Trash2Icon className="h-4 w-4" />
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  {pkg.can_delete ? 'Angebot löschen' : 'Bereits verwendete Angebote können nicht gelöscht werden.'}
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {!pkg.archived_at &&
                              pkg.stripe_price_id &&
                              pkg.billing_mode === 'subscription' && pkg.active_subscription_count > 0 && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="icon"
                                      aria-label="Aktuelle Konditionen auf Bestandsabos anwenden"
                                      onClick={() => void reviewConditions(pkg.id)}
                                      disabled={rolloutMutation.isPending}
                                    >
                                      <BadgeEuroIcon className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    Aktuelle Konditionen auf Bestandsabos anwenden
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            {pkg.failed_price_update_count > 0 && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => retryMutation.mutate(pkg.id)}
                                disabled={retryMutation.isPending}
                              >
                                <RefreshCwIcon className="h-4 w-4" />
                              </Button>
                            )}
                            {!pkg.archived_at && (
                              <>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      aria-label="Angebot archivieren"
                                      onClick={() => setArchiveTarget(pkg)}
                                    >
                                      <ArchiveIcon className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">Angebot archivieren</TooltipContent>
                                </Tooltip>
                                <Button variant="outline" size="sm" aria-label="Angebot bearbeiten"
                              onClick={() => openEditDialog(pkg)}>
                                  <PencilIcon className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TooltipProvider>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={(open) => !saveMutation.isPending && setDialogOpen(open)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialogMode === 'create' ? 'Angebot anlegen' : 'Angebot bearbeiten'}</DialogTitle>
            <DialogDescription>
              Aktive Angebote benötigen einen passenden, aktiven Stripe-Preis in EUR.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="package-name">Name</Label>
                <Input id="package-name" value={name} onChange={(event) => setName(event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Art</Label>
                <Select
                  value={billingMode}
                  onValueChange={(value) => setBillingMode(value as SubscriptionBillingMode)}
                  disabled={dialogMode === 'edit'}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="payment">Credit-Paket</SelectItem>
                    <SelectItem value="subscription">Monatsabo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="package-price">Preis in EUR</Label>
                <Input id="package-price" inputMode="decimal" value={price} onChange={(event) => setPrice(event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="stripe-price-id">Stripe-Preis-ID</Label>
                <Input
                  id="stripe-price-id"
                  value={stripePriceId}
                  onChange={(event) => setStripePriceId(event.target.value)}
                  placeholder="price_..."
                />
              </div>
              {dialogMode === 'create' && (billingMode === 'payment' ? (
                <div className="space-y-2">
                  <Label htmlFor="package-credits">Reguläre Guthabencredits</Label>
                  <Input id="package-credits" type="number" min={0} step={0.1} value={credits} onChange={(event) => setCredits(event.target.value)} />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="weekly-limit">Credits pro Woche</Label>
                  <Input
                    id="weekly-limit"
                    type="number"
                    min={0.1}
                    step={0.1}
                    value={weeklyLimit}
                    onChange={(event) => setWeeklyLimit(event.target.value)}
                    placeholder="Leer = unbegrenzt"
                  />
                  <p className="text-xs text-muted-foreground">
                    Änderungen gelten immer für alle laufenden Abos dieses Tarifs.
                  </p>
                </div>
              ))}
            </div>

            {dialogMode === 'create' && <p className="text-sm text-muted-foreground">Speichere das Angebot zunächst als Entwurf. Danach kannst du im Bearbeitungsdialog die Credit-Arten und Leistungen sowie eine benötigte Kursfreischaltung festlegen.</p>}

            <div className="space-y-2">
              <Label htmlFor="package-features">Leistungs-Stichpunkte</Label>
              <Textarea
                id="package-features"
                value={features}
                onChange={(event) => setFeatures(event.target.value)}
                rows={6}
                placeholder="Ein Stichpunkt pro Zeile"
              />
            </div>

            <div className="space-y-3 rounded-md border p-4">
              <label className="flex items-center gap-3">
                <Checkbox checked={isFeatured} onCheckedChange={(checked) => setIsFeatured(checked === true)} />
                <span className="text-sm">Als beliebtes Angebot hervorheben</span>
              </label>
              <label className="flex items-center gap-3">
                <Checkbox checked={isActive} onCheckedChange={(checked) => setIsActive(checked === true)} />
                <span className="text-sm">Für Verkauf und Landingpage aktivieren</span>
              </label>
              {billingMode === 'subscription' && dialogMode === 'edit' && (
                <>
                  <label className="flex items-start gap-3">
                    <Checkbox
                      checked={applyPriceToExisting}
                      onCheckedChange={(checked) => setApplyPriceToExisting(checked === true)}
                    />
                    <span className="text-sm">
                      Neue Konditionen ab der nächsten regulären Abrechnung auf bestehende Abos anwenden.
                      Vorgemerkte Kündigungen behalten die Kündigung, erhalten aber für eine mögliche
                      Fortsetzung ebenfalls die neuen Konditionen.
                    </span>
                  </label>
                  <p className="text-xs text-amber-600">
                    Der validierte Preis wird anschließend für Tarifwechsel in der App angeboten.
                  </p>
                </>
              )}
            </div>

            {editingPackage && <PackageBenefitsEditor benefits={benefits} requiredCourseId={requiredCourseId} billingMode={billingMode}
              onChange={(next, courseId) => { setBenefits(next); setRequiredCourseId(courseId) }} />}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={saveMutation.isPending || reviewLoading}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={saveMutation.isPending || reviewLoading}>
                {saveMutation.isPending && <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />}
                Speichern
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={review !== null} onOpenChange={(open) => { if (!open && !saveMutation.isPending && !rolloutMutation.isPending) setReview(null) }}>
        <DialogContent className="max-h-[85dvh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Konditionen für Bestandsabos übernehmen?</DialogTitle>
            <DialogDescription>Die Umstellung erfolgt je Abo zur nächsten regulären Abrechnung. Bis dahin gelten die bisherigen Konditionen. Kündigungen bleiben bestehen.</DialogDescription>
          </DialogHeader>
          {review && <ConditionsComparison preview={review.preview} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReview(null)}>Abbrechen</Button>
            <Button disabled={saveMutation.isPending || rolloutMutation.isPending || review?.preview.subscriptions.some((s) => s.conflict !== null)} onClick={() => {
              if (!review) return
              if (review.payload) saveMutation.mutate({ ...review.payload, confirmation: review.preview.confirmation })
              else rolloutMutation.mutate({ packageId: review.packageId, confirmation: review.preview.confirmation })
            }}>Konditionen verbindlich vormerken</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && !deleteMutation.isPending && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Angebot endgültig löschen?</DialogTitle>
            <DialogDescription>
              Das archivierte Angebot „{deleteTarget?.name}“ wird endgültig gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={deleteMutation.isPending} onClick={() => setDeleteTarget(null)}>
              Abbrechen
            </Button>
            <Button variant="destructive" disabled={deleteMutation.isPending} onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}>
              {deleteMutation.isPending ? 'Wird gelöscht …' : 'Endgültig löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(archiveTarget)} onOpenChange={(open) => !open && setArchiveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Angebot archivieren?</DialogTitle>
            <DialogDescription>
              Das Angebot kann danach nicht mehr neu gekauft werden. Bestehende Guthaben und Abos
              bleiben erhalten.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveTarget(null)}>Abbrechen</Button>
            <Button
              variant="destructive"
              onClick={() => archiveTarget && archiveMutation.mutate(archiveTarget.id)}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending && <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />}
              Archivieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
